// IE Hub - Dashboard Service
// Provides centralized API operations for dashboard data integration and transformation
// Enhanced with Budget Breakdown functionality for KPI tiles, charts, and insights

import { httpGet, httpPost } from '../http';
import { budgetEndpoints, customerEndpoints, onboardingEndpoints } from '../endpoints';
import { femaCapture } from '../../fema/capture';
import type {
  DashboardSummaryDto,
  ProgressSection,
  DashboardButtonActionRequest,
  CustomerProfileData,
  ShareHistoryRecord,
  SurveyData,
  IncomeDataRecord,
  ExpenseDataRecord,
  RequestConfig,
  ServiceResponse,
} from '../types';

/**
 * Dashboard Service Class
 * Handles dashboard data aggregation, transformation, and API integrations
 */
export class DashboardService {
  // Cache for performance optimization
  private static dashboardCache = new Map<string, { data: any; timestamp: number }>();
  private static readonly CACHE_TTL = 2 * 60 * 1000; // 2 minutes for dashboard data

  /**
   * Get comprehensive dashboard data for a customer
   * @param customerId - The customer ID
   * @param config - Optional request configuration
   */
  static async getDashboardData(
    customerId: string | number,
    config?: RequestConfig
  ): ServiceResponse<any> {
    try {
      const cacheKey = `dashboard_${customerId}`;
      const cached = this.dashboardCache.get(cacheKey);
      const now = Date.now();

      // Check cache first (short TTL for dashboard data)
      if (cached && (now - cached.timestamp) < this.CACHE_TTL) {
        return {
          success: true,
          data: cached.data,
          timestamp: new Date().toISOString()
        };
      }

      // Fetch calculated totals (preferred), core income data, and expense data in parallel
      const [calcTotalsResponse, incomeResponse, expenseResponse] = await Promise.all([
        httpGet<any>(customerEndpoints.getCalculatedTotals(customerId), { ...config, skipErrorCapture: true } as RequestConfig),
        httpGet<any>(budgetEndpoints.getIncomeData(customerId), { ...config, skipErrorCapture: true } as RequestConfig),
        httpGet<any>(budgetEndpoints.getExpenseData(customerId), { ...config, skipErrorCapture: true } as RequestConfig).catch(() => ({ data: null }))
      ]);

      // Normalize income payload
      const incomePayload = (incomeResponse as any)?.data;
      const incomeData: IncomeDataRecord[] | null = Array.isArray(incomePayload)
        ? incomePayload as IncomeDataRecord[]
        : (incomePayload?.data ?? null);

      if (!Array.isArray(incomeData) || incomeData.length === 0) {
        throw new Error('No income data found for customer');
      }

      // Get the latest income record for progress/breakdown and as fallback
      const latestIncome = incomeData.reduce((latest, current) => {
        return new Date(current.lastUpdateDate) > new Date(latest.lastUpdateDate) ? current : latest;
      });

      // Normalize expense payload
      const expensePayload = (expenseResponse as any)?.data;
      const expenseData: ExpenseDataRecord[] | null = Array.isArray(expensePayload)
        ? expensePayload as ExpenseDataRecord[]
        : (expensePayload?.data ?? null);
      
      // Get the latest expense record if available
      const latestExpense: ExpenseDataRecord | null = Array.isArray(expenseData) && expenseData.length > 0
        ? expenseData.reduce((latest, current) => {
            return new Date(current.lastUpdateDate) > new Date(latest.lastUpdateDate) ? current : latest;
          })
        : null;

      // Try to build summary from calculated totals first
      let summary: DashboardSummaryDto | null = null;
      try {
        const calcRaw = (calcTotalsResponse as any)?.data;
        const calcData = Array.isArray(calcRaw?.data)
          ? (calcRaw.data[0] || null)
          : (Array.isArray(calcRaw) ? (calcRaw[0] || null) : (calcRaw || null));

        if (calcData) {
          const num = (v: unknown): number => {
            const n = typeof v === 'string' ? parseFloat(v as string) : Number(v);
            return Number.isFinite(n) ? (n as number) : 0;
          };

          const totalOwingOnPriorityDebt = num(
            (calcData as any).totalOwingOnPriorityDebt ?? (calcData as any).totalOwingOnPriorityDebtV ?? 0
          );
          const totalOwingOnNonPriorityDebt = num(
            (calcData as any).totalOwingOnNonPriorityDebt ?? (calcData as any).totalOwingOnNonPriorityDebtV ?? 0
          );

          summary = {
            balanceAmount: num((calcData as any).balanceAmount ?? (calcData as any).balanceAmountV ?? 0),
            totalIncome: num((calcData as any).totalIncome ?? 0),
            totalAssets: num((calcData as any).totalAssets ?? 0),
            totalOutgoingsFixed: num((calcData as any).totalOutgoingsFixed ?? 0),
            totalOutgoingsFlexible: num((calcData as any).totalOutgoingsFlexible ?? 0),
            totalOwingOnPriorityDebt,
            totalOwingOnNonPriorityDebt,
            totalOwingOnDebts: totalOwingOnPriorityDebt + totalOwingOnNonPriorityDebt,
            firstIeDate: (calcData as any).firstIeDate || (latestIncome as any).firstIeDate,
            lastIeDate: (calcData as any).lastIeDate || (latestIncome as any).lastIeDate,
            // Compute progress using latest income and expense snapshots
            sectionsCompleted: Math.floor((this.calculateTotalFieldsFilled(latestIncome, latestExpense) / 18) * 4),
            totalFieldsToBeFilled: 18,
            totalFieldsFilled: this.calculateTotalFieldsFilled(latestIncome, latestExpense),
            completionPercentage: Math.round((this.calculateTotalFieldsFilled(latestIncome, latestExpense) / 18) * 100)
          };
        }
      } catch {
        // Ignore and fall back to income transform
      }

      // Fallback to income-based summary if calculated totals unavailable
      if (!summary) {
        summary = this.transformIncomeToDashboardSummary(latestIncome);
      }

      // Calculate progress sections
      const progressSections = this.calculateProgressSections(latestIncome, latestExpense);

      // Generate budget breakdown KPIs and charts data
      const budgetBreakdown = this.generateBudgetBreakdown(latestIncome);

      // Fetch optional service data in parallel
      const [services, shareData, surveyData] = await Promise.allSettled([
        this.getServicesData(customerId, config),
        this.getShareData(customerId, config),
        this.getSurveyData(customerId, config)
      ]);

      const dashboardData = {
        summary,
        progressSections,
        budgetBreakdown,
        services: services.status === 'fulfilled' ? services.value : this.getDefaultServices(),
        share: shareData.status === 'fulfilled' ? shareData.value : { canShare: true },
        survey: surveyData.status === 'fulfilled' ? surveyData.value : undefined
      };

      // Cache the result
      this.dashboardCache.set(cacheKey, {
        data: dashboardData,
        timestamp: now
      });

      return {
        success: true,
        data: dashboardData,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('dashboard_data_error', error, {
          category: 'api',
          severity: 'error',
          context: { customerId },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Get budget breakdown data for dashboard KPIs, charts, and insights
   * @param customerId - The customer ID
   * @param config - Optional request configuration
   */
  static async getBudgetBreakdownData(
    customerId: string | number,
    config?: RequestConfig
  ): ServiceResponse<any> {
    try {
      // Fetch core income data
      const incomeResponse = await httpGet<any>(
        budgetEndpoints.getIncomeData(customerId),
        { ...config, skipErrorCapture: true } as RequestConfig
      );

      const payload = (incomeResponse as any)?.data;
      const incomeData: IncomeDataRecord[] | null = Array.isArray(payload)
        ? payload as IncomeDataRecord[]
        : (payload?.data ?? null);

      if (!Array.isArray(incomeData) || incomeData.length === 0) {
        throw new Error('No income data found for customer');
      }

      const latestIncome = incomeData.reduce((latest, current) => {
        return new Date(current.lastUpdateDate) > new Date(latest.lastUpdateDate) ? current : latest;
      });

      // Generate comprehensive budget breakdown
      const budgetBreakdown = this.generateBudgetBreakdown(latestIncome);

      return {
        success: true,
        data: budgetBreakdown,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('budget_breakdown_data_error', error, {
          category: 'api',
          severity: 'error',
          context: { customerId },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Transform income data record into dashboard summary DTO
   * @param incomeData - The income data record
   */
  private static transformIncomeToDashboardSummary(incomeData: IncomeDataRecord): DashboardSummaryDto {
    const incomeDataAny = incomeData as any;
    
    // Calculate totals from income data
    const totalOwingOnPriorityDebt = Number(incomeDataAny.totalOwingOnPriorityDebt || 0);
    const totalOwingOnNonPriorityDebt = Number(incomeDataAny.totalOwingOnNonPriorityDebt || 0);
    const totalOwingOnDebts = totalOwingOnPriorityDebt + totalOwingOnNonPriorityDebt;

    const balanceAmount = Number(incomeDataAny.balanceAmount || 0);
    const totalIncome = Number(incomeDataAny.totalIncome || 0);
    const totalAssets = Number(incomeDataAny.totalAssets || 0);
    const totalOutgoingsFixed = Number(incomeDataAny.totalOutgoingsFixed || 0);
    const totalOutgoingsFlexible = Number(incomeDataAny.totalOutgoingsFlexible || 0);

    // Calculate progress (simplified - would need field definitions)
    const totalFieldsToBeFilled = 18; // Based on legacy dashboard
    const totalFieldsFilled = this.calculateTotalFieldsFilled(incomeData, null);
    const sectionsCompleted = Math.floor((totalFieldsFilled / totalFieldsToBeFilled) * 4); // 4 sections
    const completionPercentage = Math.round((totalFieldsFilled / totalFieldsToBeFilled) * 100);

    return {
      balanceAmount,
      totalIncome,
      totalAssets,
      totalOutgoingsFixed,
      totalOutgoingsFlexible,
      totalOwingOnPriorityDebt,
      totalOwingOnNonPriorityDebt,
      totalOwingOnDebts,
      firstIeDate: incomeDataAny.firstIeDate,
      lastIeDate: incomeDataAny.lastIeDate,
      sectionsCompleted,
      totalFieldsToBeFilled,
      totalFieldsFilled,
      completionPercentage
    };
  }

  /**
   * Generate comprehensive budget breakdown for KPIs, charts, and insights
   * @param incomeData - The income data record
   */
  private static generateBudgetBreakdown(incomeData: IncomeDataRecord): any {
    // Calculate comprehensive KPIs using the enhanced method
    const kpis = this.calculateBudgetKPIs(incomeData);

    // Generate detailed income breakdown for charts
    const incomeBreakdown = this.generateIncomeBreakdown(incomeData);

    // Generate expense breakdown for charts
    const expenseBreakdown = this.generateExpenseBreakdown(incomeData);

    // Generate debt breakdown for analysis
    const debtBreakdown = this.generateDebtBreakdown(incomeData);

    // Generate insights based on KPIs and data analysis
    const insights = this.generateBudgetInsights(incomeData, kpis);

    return {
      kpis,
      incomeBreakdown,
      expenseBreakdown,
      debtBreakdown,
      insights,
      lastUpdated: incomeData.lastUpdateDate
    };
  }


  /**
   * Generate budget insights and recommendations
   * @param incomeData - The income data record
   * @param _kpis - Calculated KPIs (unused but kept for future use)
   */
  private static generateBudgetInsights(incomeData: IncomeDataRecord, _kpis: any): any[] {
    const insights = [];
    const incomeDataAny = incomeData as any;
    const {
      totalIncome = 0,
      totalOutgoingsFixed = 0,
      totalOutgoingsFlexible = 0,
      totalAssets = 0,
      totalOwingOnPriorityDebt = 0,
      totalOwingOnNonPriorityDebt = 0,
      balanceAmount = 0
    } = incomeDataAny;

    const totalOutgoings = totalOutgoingsFixed + totalOutgoingsFlexible;
    const totalDebts = totalOwingOnPriorityDebt + totalOwingOnNonPriorityDebt;

    // Balance insights
    if (balanceAmount < 0) {
      insights.push({
        type: 'warning',
        title: 'Negative balance',
        message: 'Your expenses exceed your income. Consider reducing flexible expenses or increasing income.',
        action: 'Review budget details',
        priority: 'high'
      });
    } else if (balanceAmount > totalIncome * 0.2) {
      insights.push({
        type: 'success',
        title: 'Strong savings rate',
        message: 'You\'re saving more than 20% of your income. Great job!',
        action: 'Consider investment options',
        priority: 'low'
      });
    }

    // Debt insights
    if (totalDebts > totalIncome * 0.5) {
      insights.push({
        type: 'warning',
        title: 'High debt load',
        message: 'Your debt-to-income ratio is high. Consider debt consolidation options.',
        action: 'Explore debt advice',
        priority: 'high'
      });
    }

    // Asset insights
    if (totalAssets < totalOutgoings * 3) {
      insights.push({
        type: 'info',
        title: 'Build emergency fund',
        message: 'Consider building a 3-6 month emergency fund for financial security.',
        action: 'Set savings goals',
        priority: 'medium'
      });
    }

    // Expense insights
    if (totalOutgoingsFlexible > totalOutgoingsFixed * 1.5) {
      insights.push({
        type: 'info',
        title: 'Review flexible expenses',
        message: 'Your flexible expenses are significantly higher than fixed expenses. Look for savings opportunities.',
        action: 'Review expense categories',
        priority: 'medium'
      });
    }

    return insights;
  }

  /**
   * Calculate progress sections from income data
   * @param incomeData - The income data record
   */
  private static calculateProgressSections(incomeData: IncomeDataRecord, expenseData: ExpenseDataRecord | null): ProgressSection[] {
    return [
      {
        key: 'income',
        label: 'Income',
        totalFields: 4,
        completedFields: this.countCompletedIncomeFields(incomeData),
        icon: '💰'
      },
      {
        key: 'assets',
        label: 'Assets',
        totalFields: 2,
        completedFields: this.countCompletedAssetFields(incomeData),
        icon: '🏠'
      },
      {
        key: 'fixed-expenses',
        label: 'Fixed expenses',
        totalFields: 8,
        completedFields: expenseData ? this.countCompletedFixedExpenseCategories(expenseData) : 0,
        icon: '💸'
      },
      {
        key: 'flexible-expenses',
        label: 'Flexible expenses',
        totalFields: 4,
        completedFields: expenseData ? this.countCompletedFlexibleExpenseCategories(expenseData, incomeData) : 0,
        icon: '🛒'
      }
    ];
  }

  /**
   * Get services data (benefits, water support, open banking, marketing)
   * @param _customerId - The customer ID (unused but kept for future use)
   * @param _config - Optional request configuration (unused but kept for future use)
   */
  private static async getServicesData(
    _customerId: string | number,
    _config?: RequestConfig
  ): Promise<any> {
    try {
      // This would integrate with various services - for now return defaults
      // TODO: Implement actual service integrations as per integration plan
      return {
        benefits: { available: false, extraBenefitAmount: 0 },
        waterSupport: { available: false },
        openBanking: { available: true }, // Feature flag
        marketing: { available: false }
      };
    } catch (error) {
      // Return defaults on error
      return this.getDefaultServices();
    }
  }

  /**
   * Get share data and history
   * @param _customerId - The customer ID (unused but kept for future use)
   * @param _config - Optional request configuration (unused but kept for future use)
   */
  private static async getShareData(
    _customerId: string | number,
    _config?: RequestConfig
  ): Promise<{ history?: ShareHistoryRecord[]; canShare: boolean }> {
    try {
      // TODO: Implement actual share history API when available
      // For now return mock data structure for demonstration
      // In production, this would call: POST /api/CustomerShareHistory

      // Mock share history data for demonstration
      const mockHistory: ShareHistoryRecord[] = [
        {
          shareDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
          shareType: 'Budget PDF',
          target: 'National Debtline',
          status: 'completed'
        },
        {
          shareDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
          shareType: 'Budget Summary',
          target: 'Water Company',
          status: 'completed'
        }
      ];

      return {
        canShare: true,
        history: mockHistory
      };
    } catch (error) {
      console.warn('Failed to fetch share history:', error);
      return { canShare: true };
    }
  }

  /**
   * Get survey data if available
   * @param _customerId - The customer ID (unused but kept for future use)
   * @param _config - Optional request configuration (unused but kept for future use)
   */
  private static async getSurveyData(
    _customerId: string | number,
    _config?: RequestConfig
  ): Promise<SurveyData | undefined> {
    try {
      // TODO: Implement survey API when available
      // For now return mock survey data for demonstration

      // Mock survey data - in production this would come from API
      const mockSurvey: SurveyData = {
        surveyId: 'customer-satisfaction-2024',
        surveyTitle: 'Customer Satisfaction Survey',
        surveyUrl: 'https://survey.example.com/iehub-satisfaction',
        isAvailable: true
      };

      // Only show survey occasionally (e.g., 30% of users) or based on some criteria
      // For demo purposes, always show for now
      return Math.random() > 0.7 ? undefined : mockSurvey;

    } catch (error) {
      console.warn('Failed to fetch survey data:', error);
      return undefined;
    }
  }

  /**
   * Get customer profile data for greeting resolution
   * @param userId - The user ID
   * @param config - Optional request configuration
   */
  static async getCustomerProfile(
    userId: string | number,
    config?: RequestConfig
  ): ServiceResponse<CustomerProfileData> {
    try {
      const response = await httpGet<any>(
        onboardingEndpoints.getCustomerByUserId(userId),
        config
      );

      if (response.data.success && response.data.data) {
        return {
          success: true,
          data: response.data.data as CustomerProfileData,
          timestamp: response.data.timestamp
        };
      }

      throw new Error(response.data.message || 'Failed to fetch customer profile');

    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('dashboard_customer_profile_error', error, {
          category: 'api',
          severity: 'warning',
          context: { userId },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Log dashboard button action for analytics
   * @param actionData - The button action data
   * @param config - Optional request configuration
   */
  static async logButtonAction(
    actionData: DashboardButtonActionRequest,
    config?: RequestConfig
  ): ServiceResponse<any> {
    try {
      const response = await httpPost<any>(
        budgetEndpoints.logButtonAction(),
        actionData,
        config
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          timestamp: response.data.timestamp
        };
      }

      throw new Error(response.data.message || 'Failed to log button action');

    } catch (error) {
      // Non-blocking for analytics
      if (!config?.skipErrorCapture) {
        femaCapture('dashboard_button_action_log_error', error, {
          category: 'api',
          severity: 'warning',
          context: {
            pageName: actionData.pageName,
            subSection: actionData.subSection
          },
          skipSanitization: false,
          user: {}
        });
      }
      // Return success for analytics failures to not block UI
      return {
        success: true,
        data: null,
        timestamp: new Date().toISOString()
      };
    }
  }


  /**
   * Calculate key budget KPIs from income data
   * @param incomeData - The income data record
   */
  private static calculateBudgetKPIs(incomeData: IncomeDataRecord): any {
    const incomeDataAny = incomeData as any;
    
    // Calculate total income (normalize frequencies to monthly)
    const incomeValues = [
      incomeData.basicIncome || 0,
      incomeData.debtor2BasicIncome || 0,
      incomeData.overtimeIncome || 0,
      incomeData.debtor2OvertimeIncome || 0,
      incomeData.investmentIncome || 0,
      incomeData.debtor2InvestmentIncome || 0,
      incomeData.dividends || 0,
      incomeData.refundsAndReimbursements || 0,
      incomeData.businessIncome || 0,
      incomeData.pension || 0,
      incomeData.childBenefit || 0,
      incomeData.workingTaxCredit || 0,
      incomeData.childTaxCredit || 0,
      incomeData.empSupportAllowanceStatutory || 0,
      incomeData.dlaPipAttendanceAllowance || 0,
      incomeData.carerAllowance || 0,
      incomeData.housingBenefitLocalAllowance || 0,
      incomeData.councilTaxReduction || 0,
      incomeData.otherIncludeUniversalCredit || 0,
      incomeData.jobseekerAllowanceContribute || 0,
      incomeData.incomeSupport || 0,
      incomeData.statePension || 0,
      incomeData.privatePension || 0,
      incomeData.pensionCredit || 0,
      incomeData.pensionOthers || 0,
      incomeData.otherIncome1 || 0,
      incomeDataAny.otherIncome2 || 0,
      incomeDataAny.otherIncome3 || 0,
      incomeDataAny.otherIncome4 || 0,
      incomeDataAny.otherIncome5 || 0,
      incomeDataAny.otherIncome6 || 0,
      incomeDataAny.otherIncome7 || 0,
      incomeDataAny.otherIncome8 || 0,
      incomeDataAny.otherIncome9 || 0,
      incomeDataAny.otherIncome10 || 0
    ];

    const incomeModes = [
      incomeData.basicIncomeMode,
      incomeData.debtor2BasicIncomeMode,
      incomeData.basicIncomeMode, // overtime uses same mode as basic
      incomeData.debtor2BasicIncomeMode, // overtime uses same mode as basic
      incomeData.basicIncomeMode, // investment uses same mode as basic
      incomeData.debtor2BasicIncomeMode, // investment uses same mode as basic
      incomeData.basicIncomeMode, // dividends uses same mode as basic
      incomeData.basicIncomeMode, // refunds uses same mode as basic
      incomeData.basicIncomeMode, // business uses same mode as basic
      incomeData.statePensionMode, // pension uses same mode as state pension
      incomeData.childBenefitMode,
      incomeData.workingTaxCreditMode,
      incomeData.childTaxCreditMode,
      incomeData.empSupportAllowanceStatutoryMode,
      incomeData.dalPipAttendanceAllowanceMode,
      incomeData.carerAllowanceMode,
      incomeData.housingBenefitLocalAllowanceMode,
      incomeData.councilTaxReductionMode,
      incomeData.otherIncludeUniversalCreditMode,
      incomeData.jobseekerAllowanceContributeMode,
      incomeData.incomeSupportMode,
      incomeData.statePensionMode,
      incomeData.privatePensionMode,
      incomeData.pensionCreditMode,
      incomeData.pensionOthersMode,
      incomeData.otherIncome1Mode,
      incomeDataAny.otherIncome2Mode,
      incomeDataAny.otherIncome3Mode,
      incomeDataAny.otherIncome4Mode,
      incomeDataAny.otherIncome5Mode,
      incomeDataAny.otherIncome6Mode,
      incomeDataAny.otherIncome7Mode,
      incomeDataAny.otherIncome8Mode,
      incomeDataAny.otherIncome9Mode,
      incomeDataAny.otherIncome10Mode
    ];

    const totalIncome = this.normalizeToMonthly(incomeValues, incomeModes);

    // Calculate total expenses
    const totalExpenses = (incomeDataAny.totalOutgoingsFixed || 0) + (incomeDataAny.totalOutgoingsFlexible || 0);

    // Calculate net disposable income
    const netDisposableIncome = totalIncome - totalExpenses;

    // Calculate savings metrics
    const savingsPlanned = incomeData.monthlySavingAmount || 0;
    const savingsContribution = incomeData.monthlySavingsContribution || 0;
    const isSavingsOptIn = incomeDataAny.isMonthlyContributionToSavings === 'Y';

    // Calculate debt service ratio
    const monthlyDebtService = incomeData.monthlyTotalForCreditors || 0;
    const debtServiceRatio = totalIncome > 0 ? (monthlyDebtService / totalIncome) * 100 : 0;

    return {
      totalIncome,
      totalExpenses,
      netDisposableIncome,
      savingsPlanned,
      savingsContribution,
      isSavingsOptIn,
      monthlyDebtService,
      debtServiceRatio,
      totalAssets: incomeDataAny.totalAssets || 0,
      totalDebts: (incomeDataAny.totalOwingOnPriorityDebt || 0) + (incomeDataAny.totalOwingOnNonPriorityDebt || 0)
    };
  }

  /**
   * Generate income breakdown for charts
   * @param incomeData - The income data record
   */
  private static generateIncomeBreakdown(incomeData: IncomeDataRecord): any {
    const categories = [
      {
        category: 'Employment',
        subcategories: [
          { name: 'Basic Salary', value: this.normalizeToMonthly([incomeData.basicIncome || 0], [incomeData.basicIncomeMode || 'M']), color: '#22c55e' },
          { name: 'Overtime', value: this.normalizeToMonthly([incomeData.overtimeIncome || 0], [incomeData.basicIncomeMode || 'M']), color: '#16a34a' },
          { name: 'Investment Income', value: this.normalizeToMonthly([incomeData.investmentIncome || 0], [incomeData.basicIncomeMode || 'M']), color: '#15803d' }
        ]
      },
      {
        category: 'Benefits',
        subcategories: [
          { name: 'Child Benefit', value: this.normalizeToMonthly([incomeData.childBenefit || 0], [incomeData.childBenefitMode || 'M']), color: '#3b82f6' },
          { name: 'Working Tax Credit', value: this.normalizeToMonthly([incomeData.workingTaxCredit || 0], [incomeData.workingTaxCreditMode || 'M']), color: '#2563eb' },
          { name: 'Child Tax Credit', value: this.normalizeToMonthly([incomeData.childTaxCredit || 0], [incomeData.childTaxCreditMode || 'M']), color: '#1d4ed8' },
          { name: 'Housing Benefit', value: this.normalizeToMonthly([incomeData.housingBenefitLocalAllowance || 0], [incomeData.housingBenefitLocalAllowanceMode || 'M']), color: '#1e40af' },
          { name: 'Council Tax Reduction', value: this.normalizeToMonthly([incomeData.councilTaxReduction || 0], [incomeData.councilTaxReductionMode || 'Y']), color: '#1e3a8a' }
        ]
      },
      {
        category: 'Pensions',
        subcategories: [
          { name: 'State Pension', value: this.normalizeToMonthly([incomeData.statePension || 0], [incomeData.statePensionMode || 'M']), color: '#f59e0b' },
          { name: 'Private Pension', value: this.normalizeToMonthly([incomeData.privatePension || 0], [incomeData.privatePensionMode || 'M']), color: '#d97706' },
          { name: 'Pension Credit', value: this.normalizeToMonthly([incomeData.pensionCredit || 0], [incomeData.pensionCreditMode || 'M']), color: '#b45309' }
        ]
      }
    ];

    // Filter out empty categories and calculate totals
    const filteredCategories = categories
      .map(cat => ({
        ...cat,
        subcategories: cat.subcategories.filter(sub => sub.value > 0),
        total: cat.subcategories.reduce((sum, sub) => sum + sub.value, 0)
      }))
      .filter(cat => cat.total > 0);

    return {
      categories: filteredCategories,
      total: filteredCategories.reduce((sum, cat) => sum + cat.total, 0)
    };
  }

  /**
   * Generate expense breakdown for charts
   * @param incomeData - The income data record
   */
  private static generateExpenseBreakdown(incomeData: IncomeDataRecord): any {
    const incomeDataAny = incomeData as any;
    const fixedExpenses = incomeDataAny.totalOutgoingsFixed || 0;
    const flexibleExpenses = incomeDataAny.totalOutgoingsFlexible || 0;

    return {
      fixedExpenses,
      flexibleExpenses,
      totalExpenses: fixedExpenses + flexibleExpenses,
      ratio: fixedExpenses + flexibleExpenses > 0 ? (fixedExpenses / (fixedExpenses + flexibleExpenses)) * 100 : 0
    };
  }

  /**
   * Generate debt breakdown
   * @param incomeData - The income data record
   */
  private static generateDebtBreakdown(incomeData: IncomeDataRecord): any {
    const incomeDataAny = incomeData as any;
    const priorityDebt = incomeDataAny.totalOwingOnPriorityDebt || 0;
    const nonPriorityDebt = incomeDataAny.totalOwingOnNonPriorityDebt || 0;
    const monthlyDebtService = incomeData.monthlyTotalForCreditors || 0;

    return {
      priorityDebt,
      nonPriorityDebt,
      totalDebt: priorityDebt + nonPriorityDebt,
      monthlyDebtService,
      debtToIncomeRatio: (incomeDataAny.totalIncome || 0) > 0 ? (monthlyDebtService / (incomeDataAny.totalIncome || 0)) * 100 : 0
    };
  }


  /**
   * Normalize values to monthly frequency
   * @param values - Array of numeric values
   * @param frequencyModes - Array of frequency modes (M, 2W, 4W, Q, Y)
   */
  private static normalizeToMonthly(values: number[], frequencyModes?: string[]): number {
    if (!values || values.length === 0) return 0;

    let total = 0;
    for (let i = 0; i < values.length; i++) {
      const value = values[i] || 0;
      const mode = frequencyModes?.[i] || 'M'; // Default to monthly

      // Normalize based on frequency mode
      switch (mode) {
        case '2W': // Bi-weekly
          total += (value * 26) / 12; // 26 bi-weekly periods in a year / 12 months
          break;
        case '4W': // Monthly (4 weeks)
          total += value;
          break;
        case 'Q': // Quarterly
          total += value / 3; // Divide quarterly by 3 to get monthly
          break;
        case 'Y': // Yearly
          total += value / 12; // Divide yearly by 12 to get monthly
          break;
        case 'M': // Monthly
        default:
          total += value;
          break;
      }
    }

    return total;
  }

  /**
   * Clear dashboard cache
   * @param customerId - Optional specific customer ID to clear
   */
  static clearCache(customerId?: string | number): void {
    if (customerId) {
      this.dashboardCache.delete(`dashboard_${customerId}`);
    } else {
      this.dashboardCache.clear();
    }
  }

  // Helper methods for field calculations

  private static calculateTotalFieldsFilled(incomeData: IncomeDataRecord, expenseData: ExpenseDataRecord | null): number {
    let filled = 0;

    // Count actual completed fields instead of using boolean checks
    filled += this.countCompletedIncomeFields(incomeData);
    filled += this.countCompletedAssetFields(incomeData);
    if (expenseData) {
      filled += this.countCompletedFixedExpenseCategories(expenseData);
      filled += this.countCompletedFlexibleExpenseCategories(expenseData, incomeData);
    }

    return filled;
  }

  /**
   * Count the number of completed income fields (out of 4)
   * Fields: basicIncome, overtimeIncome, investmentIncome, pension
   */
  private static countCompletedIncomeFields(incomeData: IncomeDataRecord): number {
    let count = 0;
    if (this.isFieldCompleted(incomeData.basicIncome)) count++;
    if (this.isFieldCompleted(incomeData.overtimeIncome)) count++;
    if (this.isFieldCompleted(incomeData.investmentIncome)) count++;
    if (this.isFieldCompleted(incomeData.pension)) count++;
    return count;
  }

  /**
   * Count the number of completed asset fields (out of 2)
   * Fields: totalValueProperties, assetSavings
   */
  private static countCompletedAssetFields(incomeData: IncomeDataRecord): number {
    let count = 0;
    if (this.isFieldCompleted(incomeData.totalValueProperties)) count++;
    if (this.isFieldCompleted(incomeData.assetSavings)) count++;
    return count;
  }

  /**
   * Check if a field is considered completed (has a value)
   * A field is completed if it has a value (including 0, which is a valid budget entry)
   */
  private static isFieldCompleted(value: number | undefined | null): boolean {
    return value !== undefined && value !== null && !isNaN(Number(value));
  }


  /**
   * Count completed fixed expenses categories (out of 8)
   * Categories: household, utilities, care, transport, school, pensionInsurance, professionals, otherSourcesFixed
   */
  private static countCompletedFixedExpenseCategories(expenseData: ExpenseDataRecord): number {
    let count = 0;
    
    // Check each category by looking for at least one field with a value
    // Household category (houseRent, mortgage, secondMortgage, otherSecuredLoans, councilTax, etc.)
    if (this.isFieldCompleted(expenseData.houseRent) || 
        this.isFieldCompleted(expenseData.mortgage) || 
        this.isFieldCompleted(expenseData.secondMortgage) ||
        this.isFieldCompleted(expenseData.otherSecuredLoans) ||
        this.isFieldCompleted(expenseData.councilTax) ||
        this.isFieldCompleted(expenseData.groundRentAndServiceCharges) ||
        this.isFieldCompleted(expenseData.otherRentalAppliances) ||
        this.isFieldCompleted(expenseData.tvLicence) ||
        this.isFieldCompleted(expenseData.otherRentalAppliances) ||
        this.isFieldCompleted(expenseData.buildingContentsInsurance) ||
        this.isFieldCompleted(expenseData.otherHomeCosts)) {
      count++;
    }
    
    // Utilities category (gasBill, electricityBill, otherUtilities, waterBill, otherExpUtilities)
    if (this.isFieldCompleted(expenseData.gasBill) || 
        this.isFieldCompleted(expenseData.electricityBill) ||
        this.isFieldCompleted(expenseData.otherUtilities) ||
        this.isFieldCompleted(expenseData.waterBill) ||
        this.isFieldCompleted(expenseData.otherExpUtilities) ||
        this.isFieldCompleted(expenseData.waterWaste)) {
      count++;
    }
    
    // Care category (childCare, adultCareCosts, childMaintenance, prescriptionAndMedicine, health, otherHealthCosts)
    if (this.isFieldCompleted(expenseData.childCare) || 
        this.isFieldCompleted(expenseData.adultCareCosts) ||
        this.isFieldCompleted(expenseData.childMaintenance) ||
        this.isFieldCompleted(expenseData.prescriptionAndMedicine) ||
        this.isFieldCompleted(expenseData.health) ||
        this.isFieldCompleted(expenseData.otherHealthCosts)) {
      count++;
    }
    
    // Transport category (publicTransport, otherPublicTransport, hirePurchase, vehicleInsurance, vehicleTax, breakdownRecovery, vehicleFuel, otherCarCosts)
    if (this.isFieldCompleted(expenseData.publicTransport) ||
        this.isFieldCompleted(expenseData.otherPublicTransport) || 
        this.isFieldCompleted(expenseData.hirePurchase) ||
        this.isFieldCompleted(expenseData.vehicleInsurance) ||
        this.isFieldCompleted(expenseData.vehicleTax) ||
        this.isFieldCompleted(expenseData.breakdownRecovery) ||
        this.isFieldCompleted(expenseData.vehicleFuel) ||
        this.isFieldCompleted(expenseData.otherCarCosts) ||
        this.isFieldCompleted(expenseData.parkingCharges)) {
      count++;
    }
    
    // School category (schoolUniform, clubsAndSchoolTrips, otherSchoolCosts)
    if (this.isFieldCompleted(expenseData.schoolUniform) || 
        this.isFieldCompleted(expenseData.clubsAndSchoolTrips) ||
        this.isFieldCompleted(expenseData.otherSchoolCosts)) {
      count++;
    }
    
    // Pension/Insurance category (pensionPayments, lifeInsurance, protectionInsurance, healthInsurance, otherPensionCosts)
    if (this.isFieldCompleted(expenseData.pensionPayments) || 
        this.isFieldCompleted(expenseData.lifeInsurance) ||
        this.isFieldCompleted(expenseData.protectionInsurance) ||
        this.isFieldCompleted(expenseData.healthInsurance) ||
        this.isFieldCompleted(expenseData.otherPensionCosts)) {
      count++;
    }
    
    // Professionals category (professionalCourses, unionFees, professionalFees, otherProfessionalCosts)
    if (this.isFieldCompleted(expenseData.professionalCourses) || 
        this.isFieldCompleted(expenseData.unionFees) ||
        this.isFieldCompleted(expenseData.professionalFees) ||
        this.isFieldCompleted(expenseData.otherProfessionalCosts)) {
      count++;
    }
    
    // Other sources fixed category (otherEssentialCosts)
    if (this.isFieldCompleted(expenseData.otherEssentialCosts)) {
      count++;
    }
    
    return count;
  }

  /**
   * Count completed flexible expense categories (out of 4)
   * Categories: leisure, housekeeping, personalCosts, savings (from income data)
   */
  private static countCompletedFlexibleExpenseCategories(expenseData: ExpenseDataRecord, incomeData: IncomeDataRecord): number {
    let count = 0;
    
    // Leisure category (homePhone, mobile, hobbiesLeisureAndSport, gamblingFromHobbiesLeisure, gifts, pocketMoneyAndSchoolTrips, newspapersAndMagazines, otherCommunicationCosts)
    if (this.isFieldCompleted(expenseData.homePhone) || 
        this.isFieldCompleted(expenseData.mobile) ||
        this.isFieldCompleted(expenseData.hobbiesLeisureAndSport) ||
        this.isFieldCompleted(expenseData.gamblingFromHobbiesLeisure) ||
        this.isFieldCompleted(expenseData.gifts) ||
        this.isFieldCompleted(expenseData.pocketMoneyAndSchoolTrips) ||
        this.isFieldCompleted(expenseData.newspapersAndMagazines) ||
        this.isFieldCompleted(expenseData.otherCommunicationCosts)) {
      count++;
    }
    
    // Housekeeping category (foodAndGroceries, nappiesAndBabyItems, meals, laundryBill, beerAndAlcohol, cigarettesAndTobacco, vetBillsAndPetInsurance, homeRepairs, housekeepingOthers)
    if (this.isFieldCompleted(expenseData.foodAndGroceries) || 
        this.isFieldCompleted(expenseData.nappiesAndBabyItems) ||
        this.isFieldCompleted(expenseData.meals) ||
        this.isFieldCompleted(expenseData.laundryBill) ||
        this.isFieldCompleted(expenseData.beerAndAlcohol) ||
        this.isFieldCompleted(expenseData.cigarettesAndTobacco) ||
        this.isFieldCompleted(expenseData.vetBillsAndPetInsurance) ||
        this.isFieldCompleted(expenseData.homeRepairs) ||
        this.isFieldCompleted(expenseData.housekeepingOthers)) {
      count++;
    }
    
    // Personal costs category (clothing, hairdressing, cleaningToileteries, otherPersonalCosts)
    if (this.isFieldCompleted(expenseData.clothing) || 
        this.isFieldCompleted(expenseData.hairdressing) ||
        this.isFieldCompleted(expenseData.cleaningToileteries) ||
        this.isFieldCompleted(expenseData.otherPersonalCosts)) {
      count++;
    }
    
    // Savings category (from income data - monthlySavingAmount)
    if (this.isFieldCompleted(incomeData.monthlySavingAmount)) {
      count++;
    }
    
    return count;
  }

  private static getDefaultServices() {
    return {
      benefits: { available: false, extraBenefitAmount: 0 },
      waterSupport: { available: false },
      openBanking: { available: true },
      marketing: { available: false }
    };
  }
}

export default DashboardService;
